import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Calculator, PiggyBank, Share2 } from "lucide-react";
import { Button } from "../ui/button";

/**
 * ROICalculatorModal Component - EXACT ORIGINAL DESIGN FROM LIVE SITE
 */
export default function ROICalculatorModal({ isOpen, onClose }) {
  // Spending categories with sliders
  const [agencySpend, setAgencySpend] = useState(2000);
  const [toolsSpend, setToolsSpend] = useState(500);
  const [managerSpend, setManagerSpend] = useState(3000);
  const [videoSpend, setVideoSpend] = useState(1500);

  if (!isOpen) return null;

  // Calculate totals
  const currentMonthlyTotal = agencySpend + toolsSpend + managerSpend + videoSpend;
  const yasnoMonthly = 499; // Pro plan
  const monthlySavings = currentMonthlyTotal - yasnoMonthly;
  const yearlySavings = monthlySavings * 12;
  const savingsPercent = Math.round((monthlySavings / currentMonthlyTotal) * 100);
  
  // Determine recommended plan
  const getRecommendedPlan = () => {
    if (currentMonthlyTotal > 5000) return { name: "Enterprise Agent", price: 1499 };
    if (currentMonthlyTotal > 1000) return { name: "Pro Agent", price: 499 };
    return { name: "Starter Agent", price: 99 };
  };
  const recommendedPlan = getRecommendedPlan();

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="glass-card rounded-2xl p-6 max-w-lg w-full border border-white/10 max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                <Calculator className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <h3 className="font-semibold">Calculate Your Marketing Savings</h3>
                <p className="text-xs text-muted-foreground">See how much you could save with YASNO</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Current Spending Section */}
          <div className="mb-6">
            <h4 className="text-sm font-semibold mb-4 text-muted-foreground">What do you currently spend on marketing?</h4>
            
            {/* Agency/Freelancers Slider */}
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-2">
                <span>Agency/Freelancers</span>
                <span className="text-primary font-semibold">${agencySpend.toLocaleString()}</span>
              </div>
              <input
                type="range"
                min="0"
                max="10000"
                step="100"
                value={agencySpend}
                onChange={(e) => setAgencySpend(Number(e.target.value))}
                className="w-full accent-primary h-2 rounded-lg appearance-none cursor-pointer bg-white/10"
              />
            </div>

            {/* Content Creation Tools Slider */}
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-2">
                <span>Content Creation Tools</span>
                <span className="text-primary font-semibold">${toolsSpend.toLocaleString()}</span>
              </div>
              <input
                type="range"
                min="0"
                max="2000"
                step="50"
                value={toolsSpend}
                onChange={(e) => setToolsSpend(Number(e.target.value))}
                className="w-full accent-primary h-2 rounded-lg appearance-none cursor-pointer bg-white/10"
              />
            </div>

            {/* Social Media Manager Slider */}
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-2">
                <span>Social Media Manager</span>
                <span className="text-primary font-semibold">${managerSpend.toLocaleString()}</span>
              </div>
              <input
                type="range"
                min="0"
                max="8000"
                step="100"
                value={managerSpend}
                onChange={(e) => setManagerSpend(Number(e.target.value))}
                className="w-full accent-primary h-2 rounded-lg appearance-none cursor-pointer bg-white/10"
              />
            </div>

            {/* Video Production Slider */}
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-2">
                <span>Video Production</span>
                <span className="text-primary font-semibold">${videoSpend.toLocaleString()}</span>
              </div>
              <input
                type="range"
                min="0"
                max="5000"
                step="100"
                value={videoSpend}
                onChange={(e) => setVideoSpend(Number(e.target.value))}
                className="w-full accent-primary h-2 rounded-lg appearance-none cursor-pointer bg-white/10"
              />
            </div>

            {/* Current Monthly Total */}
            <div className="flex justify-between items-center p-3 rounded-lg bg-white/5 border border-white/10">
              <span className="font-medium">Your Current Monthly Total</span>
              <span className="text-xl font-bold">${currentMonthlyTotal.toLocaleString()}</span>
            </div>
          </div>

          {/* With YASNO Section */}
          <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 mb-4">
            <h4 className="text-sm font-semibold text-green-400 mb-3 flex items-center gap-2">
              <PiggyBank className="w-4 h-4" />
              WITH YASNO
            </h4>
            
            {/* Savings Display */}
            <div className="text-center mb-4">
              <p className="text-4xl font-bold text-green-400">${monthlySavings > 0 ? monthlySavings.toLocaleString() : 0}</p>
              <p className="text-sm text-muted-foreground">You Save Per Month</p>
              <p className="text-xs text-green-400 mt-1">${yearlySavings > 0 ? yearlySavings.toLocaleString() : 0}/year saved</p>
            </div>

            {/* Comparison Bar */}
            <div className="space-y-2 mb-4">
              <div className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground w-16">Current</span>
                <div className="flex-1 h-4 bg-red-500/30 rounded-full overflow-hidden">
                  <div className="h-full bg-red-500 rounded-full" style={{ width: '100%' }} />
                </div>
                <span className="text-xs w-20 text-right">${currentMonthlyTotal.toLocaleString()}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground w-16">YASNO</span>
                <div className="flex-1 h-4 bg-green-500/30 rounded-full overflow-hidden">
                  <div className="h-full bg-green-500 rounded-full" style={{ width: `${100 - savingsPercent}%` }} />
                </div>
                <span className="text-xs w-20 text-right">${recommendedPlan.price}</span>
              </div>
            </div>

            {/* Savings Percentage Badge */}
            {savingsPercent > 0 && (
              <div className="text-center">
                <span className="inline-block px-4 py-1 rounded-full bg-green-500/20 text-green-400 font-bold text-lg">
                  {savingsPercent}% SAVINGS
                </span>
              </div>
            )}
          </div>

          {/* Recommended Plan */}
          <div className="mb-4 p-3 rounded-lg bg-primary/10 border border-primary/20">
            <p className="text-xs text-muted-foreground mb-1">Recommended for you:</p>
            <p className="font-semibold text-primary">{recommendedPlan.name} - ${recommendedPlan.price}/mo</p>
          </div>

          {/* CTA Buttons */}
          <div className="flex gap-3">
            <Button className="flex-1 bg-green-500 hover:bg-green-600 text-white" onClick={onClose}>
              Start Saving ${monthlySavings > 0 ? monthlySavings.toLocaleString() : 0}/month
            </Button>
            <Button variant="outline" size="icon" title="Share Your Savings">
              <Share2 className="w-4 h-4" />
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
